import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import "../fonts/breuer-headline.css";
import "./leaderboard.css";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";

function readStoredUser() {
  try {
    const raw =
      localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function resolveLobby(locationState) {
  const fromState =
    typeof locationState?.lobbyName === "string" ? locationState.lobbyName.trim() : "";
  if (fromState) return fromState;
  try {
    const ss = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (ss && ss.trim()) return ss.trim();
  } catch {
    // ignore
  }
  return "";
}

/** Folder outline SVG — tab top-left */
function LeaderboardFolderOutlineSvg() {
  return (
    <svg
      className="leaderboard-folder-svg"
      viewBox="0 0 300 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      aria-hidden
    >
      <path
        d="M12,0 H148 L168,36 H288 Q300,36 300,48 V228 Q300,240 288,240 H12 Q0,240 0,228 V12 Q0,0 12,0 Z"
        fill="none"
        stroke="rgba(255,255,255,0.92)"
        strokeWidth="2.5"
      />
    </svg>
  );
}

/**
 * Brooch/medal matching Image 1:
 * - Circular badge with rank number
 * - Dashed inner ring
 * - Two ribbon tails below
 *
 * KEY FIX: rendered as a plain <div> with inline-block so it
 * sits in normal flow INSIDE the <td>, not as position:absolute
 * child. The <td> itself is position:relative and zero-width,
 * which lets the brooch overflow leftward via a negative margin.
 */
function LeaderboardBrooch({ rank }) {
  const label = String(rank ?? "");
  return (
    <div className="leaderboard-brooch">
      <svg
        className="leaderboard-brooch-svg"
        viewBox="0 0 48 72"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Outer ring */}
        <circle
          cx="24"
          cy="22"
          r="19"
          fill="rgba(255,255,255,0.10)"
          stroke="rgba(255,255,255,0.95)"
          strokeWidth="2.4"
        />
        {/* Inner dashed ring */}
        <circle
          cx="24"
          cy="22"
          r="13"
          fill="none"
          stroke="rgba(255,255,255,0.55)"
          strokeWidth="1.2"
          strokeDasharray="3.5 2.5"
        />
        {/* Rank number */}
        <text
          x="24"
          y="27"
          textAnchor="middle"
          fill="#fff"
          fontSize="14"
          fontWeight="800"
          fontFamily="Arial, sans-serif"
          stroke="rgba(0,50,80,0.4)"
          strokeWidth="0.5"
          style={{ paintOrder: "stroke fill" }}
        >
          {label}
        </text>

        {/* Ribbon bar connecting tails */}
        <line
          x1="12"
          y1="41"
          x2="36"
          y2="41"
          stroke="rgba(255,255,255,0.92)"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        {/* Left ribbon tail */}
        <path
          d="M12 41 L8 66 L18 57 L24 62"
          stroke="rgba(255,255,255,0.92)"
          strokeWidth="2"
          strokeLinejoin="round"
          fill="none"
        />
        {/* Right ribbon tail */}
        <path
          d="M36 41 L40 66 L30 57 L24 62"
          stroke="rgba(255,255,255,0.92)"
          strokeWidth="2"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

function Leaderboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const adminToken = useSelector(selectAdminToken);
  const userData = useMemo(() => readStoredUser(), []);
  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );
  const lobbyName = useMemo(() => resolveLobby(location.state), [location.state]);
  const leaderboardBackUrl = useMemo(() => {
    const fromUser = String(userData?.backButtonRedirect ?? "").trim();
    if (fromUser) return fromUser;
    const fromEnv = String(process.env.REACT_APP_BASE_URL || "").trim();
    return fromEnv || "/";
  }, [userData]);

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const currentUserId = String(
    userData.userId ??
      userData.userid ??
      userData.id ??
      userData.user_id ??
      userData.playerId ??
      userData.player_id ??
      ""
  ).trim();

  const normalizeIdentityText = (value) =>
    String(value ?? "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[^a-z0-9 ]/g, "");

  const userNameCandidates = useMemo(() => {
    const firstName = String(userData.firstName ?? userData.first_name ?? "").trim();
    const lastName = String(userData.lastName ?? userData.last_name ?? "").trim();
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    const userId = String(
      userData.userId ??
        userData.userid ??
        userData.id ??
        userData.user_id ??
        userData.playerId ??
        userData.player_id ??
        ""
    ).trim();
    const email = String(userData.email ?? "").trim();
    const emailPrefix = email.includes("@") ? email.split("@")[0].trim() : "";
    const directName = String(userData.name ?? "").trim();

    const raw = [directName, fullName, firstName, lastName, userId, emailPrefix];
    const normalized = raw
      .map(normalizeIdentityText)
      .filter(Boolean)
      .filter((value, idx, arr) => arr.indexOf(value) === idx);
    return normalized;
  }, [userData]);

  const isCurrentUserRow = (row) => {
    const rowUserId = String(row?.userid ?? "").trim();
    if (currentUserId && rowUserId === currentUserId) return true;

    const rowName = normalizeIdentityText(row?.name ?? "");
    if (!rowName || userNameCandidates.length === 0) return false;

    return userNameCandidates.some(
      (candidate) =>
        candidate === rowName ||
        candidate.includes(rowName) ||
        rowName.includes(candidate)
    );
  };

  const visibleRows = useMemo(() => {
    const topFive = list.slice(0, 5).map((row) => ({
      ...row,
      displayRank: row.rank,
    }));
    if (!list.length) return [];

    const matchingByUserId = currentUserId
      ? list.find((row) => String(row?.userid ?? "").trim() === currentUserId)
      : null;
    const matchingByName = list.find((row) => isCurrentUserRow(row));
    const matchingRow = matchingByUserId || matchingByName;

    if (!matchingRow) return topFive;

    const alreadyInTopFive = topFive.some((row) => row.rank === matchingRow.rank);
    if (alreadyInTopFive) return topFive;

    return [
      ...topFive,
      {
        ...matchingRow,
        displayRank: matchingRow.rank,
      },
    ];
  }, [list, currentUserId]);

  useEffect(() => {
    dispatch(setBackButtonUrl(leaderboardBackUrl));
  }, [dispatch, leaderboardBackUrl]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const hadCommon = body.classList.contains("common-bg");
    html.classList.add("leaderboard-static-bg");
    body.classList.add("leaderboard-static-bg");
    body.classList.remove("common-bg");
    return () => {
      html.classList.remove("leaderboard-static-bg");
      body.classList.remove("leaderboard-static-bg");
      if (hadCommon) body.classList.add("common-bg");
    };
  }, []);

  useEffect(() => {
    const sessionId = userData.sessionId;
    const organizationId = userData.organizationId;
    if (!adminToken || !backendBase || !sessionId || !organizationId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendBase}/getLeaderboard`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ lobby: lobbyName || "" }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success && Array.isArray(data.data)) {
          setList(data.data);
        } else {
          setError(data.message || "Failed to load leaderboard");
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load leaderboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, backendBase, lobbyName, userData.organizationId, userData.sessionId]);

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-gridOverlay" aria-hidden />
      <div className="leaderboard-splitOverlay" aria-hidden />

      <div className="leaderboard-top-bar" />

      <div className="leaderboard-container">
        <div className="leaderboard-folder-wrap">
          <div className="leaderboard-folder">
            <LeaderboardFolderOutlineSvg />
            <span className="leaderboard-folder-tab">LEADERBOARD</span>

            <div className="leaderboard-folder-body">
              {loading ? (
                <div className="leaderboard-loading">Loading...</div>
              ) : error ? (
                <div className="leaderboard-error">{error}</div>
              ) : (
                <div className="leaderboard-table-scroll">
                  <table className="leaderboard-table" aria-label="Leaderboard">
                    <thead>
                      <tr>
                        <th className="leaderboard-th-rank" scope="col">
                          <span className="leaderboard-visually-hidden">Rank</span>
                        </th>
                        <th scope="col">Name</th>
                        <th scope="col">Points</th>
                        <th scope="col">Time </th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="leaderboard-empty">
                            No players yet.
                          </td>
                        </tr>
                      ) : (
                        visibleRows.map((row) => (
                          <tr
                            key={`${row.rank}-${row.name}-${row.points}`}
                            className={isCurrentUserRow(row) ? "leaderboard-current-user-row" : ""}
                          >
                            <td className="leaderboard-rank-cell">
                              <LeaderboardBrooch rank={row.displayRank ?? row.rank} />
                            </td>
                            <td className="leaderboard-td-name">{row.name}</td>
                            <td className="leaderboard-td-num">{row.points}</td>
                            <td className="leaderboard-td-num">{row.time || "00:00"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
